import { PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'next-i18next'
import ModalWrapper from '@/components/ModalWrapper'
import SmartLink from '@components/SmartLink'
import { Agreement, ConsentItem } from '@/hooks/useTosV2'
import styles from './index.module.sass'

interface ModalInterface {
  serviceName: string;
  consentItems: Array<ConsentItem>;
  onSubmit: (agreements: Agreement[]) => void;
  onCancel: () => void;
}

const TermsCheckModal = ({
  serviceName,
  consentItems = [],
  onSubmit,
  onCancel
}: PropsWithChildren<ModalInterface>) => {
  const { t } = useTranslation()
  const [agreements, setArgements] = useState(() => consentItems.reduce((agreementMap, item) => {
    agreementMap[item.termCode] = item.defaultChecked
    return agreementMap
  }, {}))

  const checked = useMemo(() => consentItems.every(({ termCode, required }) => required ? agreements[termCode] : true), [consentItems, agreements])

  const agreeCall = useCallback(() => {
    const userAgreements = Object.entries(agreements).map(([termCode, agreed]) => ({ termCode, agreed: agreed as boolean }))
    onSubmit(userAgreements)
  }, [agreements, onSubmit])

  if (consentItems.length === 0) return <></>

  return (
    <ModalWrapper>
      <div className={styles.termsCheckModal}>
        <div className={styles.termsCheckModalCloseButton} onClick={onCancel} />
        <div className={styles.termsCheckModalTitle}>{t('client.liveview.popuptitle.termsofuse', { serviceName })}</div>
        <div className={styles.termsCheckModalDescription}>{t('client.liveview.popupdesc.pptos')}</div>
        <div className={styles.termsCheckModalContent}>
          {
            consentItems.map(({ termCode, title, subTitle, contentUrl }, index) => {
              return (
                <div key={`${termCode}-${index}`} className={styles.termsCheckModalItem}>
                  <span
                    className={ agreements[termCode] ? styles.termsCheckModalBoxChecked : styles.termsCheckModalBox }
                    onClick={() => {
                      agreements[termCode] = !agreements[termCode]
                      setArgements({ ...agreements })
                    }}
                  />
                  <div className={styles.termsCheckModalInfo}>
                    <SmartLink href={contentUrl} className={styles.termsCheckModalLink}>{title}</SmartLink>
                    { subTitle ? <div>({subTitle})</div> : <></> }
                  </div>
                </div>
              )
            })
          }
        </div>
        <button
          type="button"
          className={checked ? styles.termsCheckModalButtonActive : styles.termsCheckModalButton }
          disabled={!checked}
          onClick={agreeCall}
        >
          {t('client.liveview.popupbutton.accept')}
        </button>
      </div>
    </ModalWrapper>
  )
}

TermsCheckModal.defaultProps = {
  title: '',
  description: '',
  subDescription: '',
  checked: false
}

export default TermsCheckModal
